import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Eye, Loader2, Search, Trash2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import StatusBadge from "@/components/StatusBadge";
import { api, apiError } from "@/lib/api";
import { relative } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const FILTERS = ["all", "draft", "sent", "viewed", "accepted", "declined", "expired"];

export default function Proposals() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({ total: 0, pages: 1, page: 1 });
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = () =>
    api
      .get("/proposals", { params: { status, q: q || undefined, page, page_size: 25 } })
      .then((r) => {
        setRows(r.data.items);
        setMeta({ total: r.data.total, pages: r.data.pages, page: r.data.page });
      })
      .catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [status, q, page]);

  useEffect(() => {
    setPage(1);
  }, [status, q]);

  const counts = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => (m[r.status] = (m[r.status] || 0) + 1));
    return m;
  }, [rows]);

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/proposals/${id}/duplicate`);
      toast.success("Duplicated");
      navigate(`/editor/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/proposals/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell title="Proposals" subtitle="Your full pipeline, colour-coded by stage">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <Input
            data-testid="proposal-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search proposals"
            className="pl-9 h-9 bg-zinc-900/60 border-zinc-800 text-sm"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto nv-scroll">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}-btn`}
              onClick={() => setStatus(f)}
              className={`px-3 h-9 rounded-lg text-xs whitespace-nowrap border transition-colors ${
                status === f ? "border-indigo-500/40 bg-indigo-500/12 text-indigo-300" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {f}
              {f !== "all" && counts[f] ? <span className="ml-1.5 font-num text-zinc-600">{counts[f]}</span> : null}
            </button>
          ))}
        </div>
      </div>

      {!rows ? (
        <div className="h-48 grid place-items-center text-zinc-600">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : !rows.length ? (
        <div data-testid="proposals-empty" className="nv-card p-12 text-center">
          <p className="text-zinc-300 font-medium">No proposals yet</p>
          <p className="text-sm text-zinc-500 mt-1">Start with the AI Studio or pick a template.</p>
        </div>
      ) : (
        <div data-testid="proposals-table" className="rounded-xl border border-zinc-800/80 bg-panel overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500 border-b border-zinc-800/80">
            <div className="col-span-4">Proposal</div>
            <div className="col-span-2">Client</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Value</div>
            <div className="col-span-2 text-right">Updated</div>
          </div>
          <div className="divide-y divide-zinc-800/60">
            {rows.map((p) => (
              <div key={p.id} data-testid={`proposal-row-${p.id}`} className="group grid md:grid-cols-12 gap-2 md:gap-3 px-4 py-3 items-center hover:bg-zinc-800/25 transition-colors">
                <button onClick={() => navigate(`/editor/${p.id}`)} className="md:col-span-4 text-left min-w-0">
                  <p className="text-sm text-zinc-100 truncate group-hover:text-indigo-300 transition-colors">{p.title}</p>
                  <p className="text-[11px] text-zinc-500 font-num">{p.block_count} blocks</p>
                </button>
                <div className="md:col-span-2 text-sm text-zinc-400 truncate">{p.client_name}</div>
                <div className="md:col-span-2">
                  <StatusBadge status={p.status} testid={`proposal-status-${p.id}`} />
                </div>
                <div className="md:col-span-2 text-sm font-num text-zinc-200 md:text-right">{p.total_formatted}</div>
                <div className="md:col-span-2 flex items-center justify-end gap-1">
                  <span className="text-[11px] text-zinc-500 font-num mr-1">{relative(p.updated_at)}</span>
                  {p.share?.token && !p.share.revoked && (
                    <button data-testid={`open-share-${p.id}`} onClick={() => window.open(`/p/${p.share.token}`, "_blank")} className="p-1.5 rounded hover:bg-zinc-800" title="Open client view">
                      <Eye className="w-3.5 h-3.5 text-zinc-500" />
                    </button>
                  )}
                  <button data-testid={`duplicate-${p.id}`} onClick={() => duplicate(p.id)} className="p-1.5 rounded hover:bg-zinc-800" title="Duplicate">
                    <Copy className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                  <button data-testid={`delete-${p.id}`} onClick={() => remove(p.id)} className="p-1.5 rounded hover:bg-red-500/15" title="Delete">
                    <Trash2 className="w-3.5 h-3.5 text-zinc-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {meta.pages > 1 && (
        <div data-testid="proposals-pagination" className="mt-4 flex items-center justify-between text-xs text-zinc-500">
          <span className="font-num">
            Page {meta.page} of {meta.pages} · {meta.total} proposals
          </span>
          <div className="flex gap-2">
            <button
              data-testid="prev-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 h-8 rounded-lg border border-zinc-800 hover:text-zinc-200 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              data-testid="next-page-btn"
              disabled={page >= meta.pages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 h-8 rounded-lg border border-zinc-800 hover:text-zinc-200 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
